

import { Pencil, Trash2 } from "lucide-react";
import Badge from "@/app/components/Badge";
import { formatDate } from "@/lib/data";

export interface EnquiryData {
  id: number;
  name: string;
  phone: string;
  email: string;
  event_date: string;
  guests: number;
  guest_count?: number;
  event_type: string;
  type?: string;
  status: string;
  notes: string;
  lead_source: string;
  created_at: string;
  client_id?: number;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  package_id?: number;
  estimated_budget?: number;
}

interface EnquiryTableProps {
  enquiries: EnquiryData[];
  isStaff: boolean;
  canDelete: boolean;
  onEdit: (enquiry: EnquiryData) => void;
  onConvert: (enquiry: EnquiryData) => void;
  onDelete: (id: number) => void;
  onViewDetails?: (id: number) => void;
}

export default function EnquiryTable({
  enquiries,
  isStaff,
  canDelete,
  onEdit,
  onConvert,
  onDelete,
  onViewDetails,
}: EnquiryTableProps) {
  return (
    <div className="hidden md:block bg-white rounded-xl border border-stone-100 overflow-hidden mb-6 shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50">
              <th className="text-left px-5 py-4 text-xs font-semibold text-stone-600 uppercase tracking-wide">Client</th>
              <th className="text-left px-5 py-4 text-xs font-semibold text-stone-600 uppercase tracking-wide">Type</th>
              <th className="text-left px-5 py-4 text-xs font-semibold text-stone-600 uppercase tracking-wide">Event Date</th>
              <th className="text-center px-5 py-4 text-xs font-semibold text-stone-600 uppercase tracking-wide">Guests</th>
              <th className="text-center px-5 py-4 text-xs font-semibold text-stone-600 uppercase tracking-wide">Status</th>
              <th className="text-left px-5 py-4 text-xs font-semibold text-stone-600 uppercase tracking-wide">Received</th>
              <th className="text-right px-5 py-4 text-xs font-semibold text-stone-600 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {enquiries.map((e: any) => {
              const name = e.client_name || e.client?.name || e.name || "Unknown";
              const phone = e.client_phone || e.client?.phone || e.phone || "";
              const eventType = e.type || e.event_type || "-";
              const dateStr = e.event_date ? formatDate(e.event_date) : "-";
              const guests = e.guest_count || e.guests || "-";

              return (
              <tr key={e.id} className="hover:bg-amber-50 transition-colors border-stone-50">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold bg-amber-500">
                      {name.charAt(0) || "?"}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-stone-900 truncate">{name}</div>
                      <div className="text-xs text-stone-400 truncate">{phone}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-stone-700 font-medium">{eventType}</td>
                <td className="px-5 py-4 text-stone-700">{dateStr}</td>
                <td className="px-5 py-4 text-center text-stone-700 font-medium">{guests}</td>
                <td className="px-5 py-4 text-center"><Badge label={e.status} status={e.status} /></td>
                <td className="px-5 py-4 text-stone-500 text-xs font-medium">{e.created_at ? formatDate(e.created_at) : "-"}</td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => onEdit(e)}
                      className="p-2 rounded-lg text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {(e.status === 'new' || e.status === 'qualified') && (
                      <button
                        onClick={() => onConvert(e)}
                        className="text-xs px-3 py-2 rounded-lg text-white font-semibold bg-amber-500 hover:bg-amber-600 transition-colors"
                      >
                        Convert
                      </button>
                    )}
                    {!isStaff && canDelete && (
                      <button
                        onClick={() => onDelete(e.id)}
                        className="p-2 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {enquiries.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-stone-400 text-sm">No enquiries found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
